import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, IconButton, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Tabs, Tab
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import workoutService from '../services/workoutService';

const PendingPayments = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState({});
  const [editablePercentages, setEditablePercentages] = useState({});
  const [editingPercentages, setEditingPercentages] = useState({});
  const [tempPercentages, setTempPercentages] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTrainerName, setSelectedTrainerName] = useState('');

  const [showPayoutDetailModal, setShowPayoutDetailModal] = useState(false);
  const [payoutDetail, setPayoutDetail] = useState(null);
  const [payoutDetailLoading, setPayoutDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  const [payoutNotes, setPayoutNotes] = useState({});

  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [breakdownPayment, setBreakdownPayment] = useState(null);

  useEffect(() => {
    dispatch(login(user));
    fetchPendingPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await workoutService.getPendingPayments();
      setPendingPayments(data);
      const initialPercentages = {};
      data.forEach(payment => { initialPercentages[payment.sellerId] = payment.sellerIncomePercentage; });
      setEditablePercentages(initialPercentages);
    } catch (err) {
      setError('Failed to fetch pending payments: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayout = async (sellerId) => {
    if (!window.confirm('Process this payout? This will transfer funds via Stripe.')) return;
    setPayoutLoading(prev => ({ ...prev, [sellerId]: true }));
    try {
      const result = await workoutService.payoutSeller(sellerId, payoutNotes[sellerId] || '');
      alert(`Payout successful!\nTotal: $${result.totalPayoutAmount}\nAthlete Earnings: $${result.breakdown?.athleteEarnings?.toFixed(2)}\nReferral Commissions: $${result.breakdown?.referralCommissions?.toFixed(2)}\nTransfer ID: ${result.stripeTransferId}`);
      setPayoutNotes(prev => ({ ...prev, [sellerId]: '' }));
      await fetchPendingPayments();
    } catch (err) {
      alert('Failed to process payout: ' + (err.response?.data?.message || err.message));
    } finally {
      setPayoutLoading(prev => ({ ...prev, [sellerId]: false }));
    }
  };

  const handleEditPercentage = (sellerId) => {
    setEditingPercentages(prev => ({ ...prev, [sellerId]: true }));
    setTempPercentages(prev => ({ ...prev, [sellerId]: editablePercentages[sellerId] || 0 }));
  };

  const handleSavePercentage = async (sellerId) => {
    const newPercentage = tempPercentages[sellerId] || 0;
    try {
      await workoutService.updateIncomePercentage(sellerId, newPercentage);
      setEditablePercentages(prev => ({ ...prev, [sellerId]: newPercentage }));
      setEditingPercentages(prev => ({ ...prev, [sellerId]: false }));
      alert('Income percentage updated successfully!');
    } catch (err) { alert('Failed to update income percentage: ' + err.message); }
  };

  const handleCancelEdit = (sellerId) => {
    setEditingPercentages(prev => ({ ...prev, [sellerId]: false }));
    setTempPercentages(prev => ({ ...prev, [sellerId]: editablePercentages[sellerId] || 0 }));
  };

  const handleShowBreakdown = (payment) => { setBreakdownPayment(payment); setShowBreakdownModal(true); };
  const handleCloseBreakdown = () => { setShowBreakdownModal(false); setBreakdownPayment(null); };

  const handleShowDetails = (payment) => { setSelectedPayment(payment); setShowDetailsModal(true); setDateFrom(''); setDateTo(''); };
  const handleCloseDetails = () => { setShowDetailsModal(false); setSelectedPayment(null); setDateFrom(''); setDateTo(''); };

  const handleShowHistory = async (sellerId, sellerName) => {
    setSelectedTrainerName(sellerName);
    setHistoryLoading(true);
    setShowHistoryModal(true);
    try {
      const data = await workoutService.getPayoutHistory(sellerId);
      setPayoutHistory(data);
    } catch (err) { alert('Failed to fetch payout history: ' + err.message); }
    finally { setHistoryLoading(false); }
  };

  const handleShowPayoutDetail = async (payoutId) => {
    setPayoutDetailLoading(true);
    setShowPayoutDetailModal(true);
    setDetailTab(0);
    try {
      const data = await workoutService.getPayoutDetails(payoutId);
      setPayoutDetail(data);
    } catch (err) { alert('Failed to fetch payout details: ' + err.message); }
    finally { setPayoutDetailLoading(false); }
  };

  const filterPaymentsByDate = (payments) => {
    if (!payments) return [];
    return payments.filter(payment => {
      if (!payment.createdAt) return false;
      const paymentDate = new Date(payment.createdAt);
      if (dateFrom && paymentDate < new Date(dateFrom)) return false;
      if (dateTo && paymentDate > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
  };

  const formatCurrency = (val) => '$' + Number(val || 0).toFixed(2);

  const getStatusChip = (status) => {
    const map = {
      pending: { color: '#ffb74d', bg: 'rgba(255,152,0,0.15)' },
      processing: { color: '#64b5f6', bg: 'rgba(100,181,246,0.15)' },
      completed: { color: '#81c784', bg: 'rgba(76,175,80,0.15)' },
      failed: { color: '#e57373', bg: 'rgba(244,67,54,0.15)' },
      paid_out: { color: '#81c784', bg: 'rgba(76,175,80,0.15)' },
    };
    const s = map[status] || { color: '#8b949e', bg: 'rgba(139,148,158,0.15)' };
    return <Chip label={status} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600 }} />;
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={fetchPendingPayments} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Athlete</TableCell>
                <TableCell>Stripe</TableCell>
                <TableCell>Channel %</TableCell>
                <TableCell>Gross Revenue</TableCell>
                <TableCell>Platform Fee</TableCell>
                <TableCell>Athlete Payout</TableCell>
                <TableCell>Referral Deducted</TableCell>
                <TableCell>Referral Earned</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingPayments.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: '#8b949e' }}>No payments found.</TableCell></TableRow>
              ) : pendingPayments.map((payment, index) => {
                const hasPending = payment.payments?.some(p => !p.payout) || payment.pendingReferralEarnings > 0;
                const isReferrerOnly = !!payment.isReferrerOnly;
                return (
                  <TableRow key={index} sx={{ opacity: !hasPending ? 0.5 : 1 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{payment.sellerName}</Typography>
                      <Typography variant="caption" sx={{ color: '#8b949e' }}>{payment.sellerId?.slice(-6)}</Typography>
                      {isReferrerOnly && (
                        <Chip label="Referrer Only" size="small"
                          sx={{ ml: 0.5, bgcolor: 'rgba(144,202,249,0.15)', color: '#90caf9', fontWeight: 600, fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={payment.sellerStripeAccount ? 'Connected' : 'No Account'} size="small"
                          sx={{ bgcolor: payment.sellerStripeAccount ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)', color: payment.sellerStripeAccount ? '#81c784' : '#ffb74d', fontWeight: 600, fontSize: '0.7rem' }} />
                        <Chip label={payment.sellerStripePayoutsEnabled ? 'Payouts On' : 'Payouts Off'} size="small"
                          sx={{ bgcolor: payment.sellerStripePayoutsEnabled ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)', color: payment.sellerStripePayoutsEnabled ? '#81c784' : '#e57373', fontWeight: 600, fontSize: '0.7rem' }} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isReferrerOnly ? (
                        <Typography variant="caption" sx={{ color: '#8b949e' }}>—</Typography>
                      ) : editingPercentages[payment.sellerId] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField size="small" type="number" value={tempPercentages[payment.sellerId] || 0}
                            onChange={(e) => setTempPercentages(prev => ({ ...prev, [payment.sellerId]: parseFloat(e.target.value) || 0 }))}
                            sx={{ width: 65 }} inputProps={{ min: 0, max: 100, step: 0.01 }} />
                          <Typography variant="body2">%</Typography>
                          <IconButton size="small" color="success" onClick={() => handleSavePercentage(payment.sellerId)}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                          <IconButton size="small" onClick={() => handleCancelEdit(payment.sellerId)} sx={{ color: '#8b949e' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{editablePercentages[payment.sellerId] || payment.sellerIncomePercentage}%</span>
                          <IconButton size="small" onClick={() => handleEditPercentage(payment.sellerId)} sx={{ color: '#8b949e', p: 0.3 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{isReferrerOnly ? <Typography variant="caption" sx={{ color: '#8b949e' }}>—</Typography> : formatCurrency(payment.totalAmount)}</TableCell>
                    <TableCell>{isReferrerOnly ? <Typography variant="caption" sx={{ color: '#8b949e' }}>—</Typography> : formatCurrency(payment.totalPlatformFee)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{isReferrerOnly ? <Typography variant="caption" sx={{ color: '#8b949e' }}>—</Typography> : formatCurrency(payment.totalAthleteAmount)}</TableCell>
                    <TableCell>{isReferrerOnly ? <Typography variant="caption" sx={{ color: '#8b949e' }}>—</Typography> : formatCurrency(payment.totalReferrerAmount)}</TableCell>
                    <TableCell>
                      {payment.pendingReferralEarnings > 0
                        ? <Typography variant="body2" sx={{ color: '#81c784' }}>{formatCurrency(payment.pendingReferralEarnings)}</Typography>
                        : <Typography variant="body2" sx={{ color: '#8b949e' }}>$0.00</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Button size="small" variant="contained" color="success" onClick={() => handlePayout(payment.sellerId)}
                            disabled={payoutLoading[payment.sellerId] || editingPercentages[payment.sellerId] || !payment.sellerStripeAccount || !payment.sellerStripePayoutsEnabled || !hasPending}
                            sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>
                            {payoutLoading[payment.sellerId] ? <CircularProgress size={14} /> : 'Payout'}
                          </Button>
                          {!isReferrerOnly && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleShowBreakdown(payment)}
                                title="How is this payout calculated?"
                                sx={{ color: '#2bb6a9', p: 0.3 }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                              <Button size="small" variant="outlined" onClick={() => handleShowDetails(payment)} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>Details</Button>
                              <Button size="small" variant="outlined" onClick={() => handleShowHistory(payment.sellerId, payment.sellerName)} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem', color: '#8b949e', borderColor: 'rgba(255,255,255,0.1)' }}>History</Button>
                            </>
                          )}
                        </Box>
                        <TextField size="small" placeholder="Payout notes (optional)" value={payoutNotes[payment.sellerId] || ''}
                          onChange={(e) => setPayoutNotes(prev => ({ ...prev, [payment.sellerId]: e.target.value }))}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payment Details Dialog */}
      <Dialog open={showDetailsModal} onClose={handleCloseDetails} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Payment Details — {selectedPayment?.sellerName}
          <IconButton onClick={handleCloseDetails} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {[
                  { label: 'Gross Revenue', value: formatCurrency(selectedPayment.totalAmount) },
                  { label: 'Platform Fee', value: formatCurrency(selectedPayment.totalPlatformFee), color: '#2bb6a9' },
                  { label: 'Athlete Payout', value: formatCurrency(selectedPayment.totalAthleteAmount), color: '#81c784' },
                  { label: 'Referral Deducted', value: formatCurrency(selectedPayment.totalReferrerAmount), color: '#ffb74d' },
                ].map((card) => (
                  <Paper key={card.label} sx={{ flex: 1, minWidth: 160, p: 2, textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Typography variant="caption" sx={{ color: '#8b949e' }}>{card.label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: card.color || '#e4e6eb' }}>{card.value}</Typography>
                  </Paper>
                ))}
              </Box>

              {selectedPayment.payments?.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#8b949e' }}>From:</Typography>
                    <TextField size="small" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
                    <Typography variant="caption" sx={{ color: '#8b949e' }}>To:</Typography>
                    <TextField size="small" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Buyer</TableCell><TableCell>Workout / Source</TableCell><TableCell>Type</TableCell>
                          <TableCell>Gross</TableCell><TableCell>Net</TableCell><TableCell>Platform Fee</TableCell>
                          <TableCell>Athlete</TableCell><TableCell>Referral</TableCell><TableCell>Date</TableCell><TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filterPaymentsByDate(selectedPayment.payments).map((p, index) => (
                          <TableRow key={index}>
                            <TableCell>{p.buyerName}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{p.workoutTitle || '-'}</TableCell>
                            <TableCell><Chip label={p.sourceType || 'subscription'} size="small" sx={{ bgcolor: 'rgba(139,148,158,0.15)', color: '#8b949e', fontSize: '0.7rem' }} /></TableCell>
                            <TableCell>{formatCurrency(p.amount)}</TableCell>
                            <TableCell>{formatCurrency(p.netAmount)}</TableCell>
                            <TableCell>{formatCurrency(p.platformFeeAmount)}</TableCell>
                            <TableCell>{formatCurrency(p.athleteAmount)}</TableCell>
                            <TableCell>
                              {p.referrerAmount > 0 ? <>{formatCurrency(p.referrerAmount)} <Typography variant="caption" sx={{ color: '#8b949e' }}>{p.referrerName ? '→ ' + p.referrerName : ''}</Typography></> : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</TableCell>
                            <TableCell>{getStatusChip(p.payoutStatus || (p.payout ? 'paid_out' : 'pending'))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" sx={{ color: '#8b949e', mt: 1, display: 'block' }}>
                    Showing {filterPaymentsByDate(selectedPayment.payments).length} of {selectedPayment.payments.length} payments
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetails} sx={{ color: '#8b949e' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payout History Dialog */}
      <Dialog open={showHistoryModal} onClose={() => setShowHistoryModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Payout History — {selectedTrainerName}
          <IconButton onClick={() => setShowHistoryModal(false)} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>
          ) : payoutHistory.length === 0 ? (
            <Typography sx={{ color: '#8b949e' }}>No payout history found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell>Total Payout</TableCell>
                    <TableCell>Athlete Earnings</TableCell><TableCell>Referral Commissions</TableCell>
                    <TableCell>Platform Fees</TableCell><TableCell>Stripe Transfer</TableCell><TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payoutHistory.map((record, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}</TableCell>
                      <TableCell>{getStatusChip(record.status)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(record.totalPayoutAmount)}</TableCell>
                      <TableCell>{formatCurrency(record.athleteEarningsTotal)}</TableCell>
                      <TableCell>{formatCurrency(record.referralCommissionsTotal)}</TableCell>
                      <TableCell>{formatCurrency(record.platformFeeTotal)}</TableCell>
                      <TableCell>{record.stripeTransferId ? <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{record.stripeTransferId}</Typography> : '-'}</TableCell>
                      <TableCell><Button size="small" variant="outlined" onClick={() => handleShowPayoutDetail(record._id)} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>View</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowHistoryModal(false)} sx={{ color: '#8b949e' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payout Detail Dialog */}
      <Dialog open={showPayoutDetailModal} onClose={() => setShowPayoutDetailModal(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Payout Record Detail
          <IconButton onClick={() => setShowPayoutDetailModal(false)} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {payoutDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>
          ) : payoutDetail ? (
            <Box>
              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2, '& .MuiTab-root': { color: '#8b949e' }, '& .Mui-selected': { color: '#2bb6a9' } }}>
                <Tab label="Summary" />
                <Tab label={`Payments (${payoutDetail.payments?.length || 0})`} />
                <Tab label={`Referral Earnings (${payoutDetail.referralEarnings?.length || 0})`} />
              </Tabs>

              {detailTab === 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Paper sx={{ flex: 1, minWidth: 280, p: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Table size="small">
                      <TableBody>
                        {[
                          ['Trainer', payoutDetail.payoutRecord?.trainerName],
                          ['Status', getStatusChip(payoutDetail.payoutRecord?.status)],
                          ['Stripe Transfer', <Typography key="st" variant="caption" sx={{ fontFamily: 'monospace' }}>{payoutDetail.payoutRecord?.stripeTransferId || '-'}</Typography>],
                          ['Period', `${payoutDetail.payoutRecord?.periodStart ? new Date(payoutDetail.payoutRecord.periodStart).toLocaleDateString() : '?'} – ${payoutDetail.payoutRecord?.periodEnd ? new Date(payoutDetail.payoutRecord.periodEnd).toLocaleDateString() : '?'}`],
                          ['Triggered By', payoutDetail.payoutRecord?.triggeredByName],
                          ['Notes', payoutDetail.payoutRecord?.notes || '-'],
                          ...(payoutDetail.payoutRecord?.failureReason ? [['Failure Reason', <Typography key="fr" sx={{ color: '#e57373' }}>{payoutDetail.payoutRecord.failureReason}</Typography>]] : []),
                        ].map(([label, value], i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontWeight: 600, border: 'none', py: 0.5 }}>{label}</TableCell>
                            <TableCell sx={{ border: 'none', py: 0.5 }}>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                  <Paper sx={{ flex: 1, minWidth: 280, p: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Table size="small">
                      <TableBody>
                        {[
                          ['Total Gross', formatCurrency(payoutDetail.payoutRecord?.totalGrossEarnings)],
                          ['Stripe Fees', formatCurrency(payoutDetail.payoutRecord?.totalStripeFees)],
                          ['Total Net', formatCurrency(payoutDetail.payoutRecord?.totalNetEarnings)],
                          ['Platform Fee', formatCurrency(payoutDetail.payoutRecord?.platformFeeTotal)],
                          ['Athlete Earnings', formatCurrency(payoutDetail.payoutRecord?.athleteEarningsTotal)],
                          ['Referral Commissions', formatCurrency(payoutDetail.payoutRecord?.referralCommissionsTotal)],
                        ].map(([label, value], i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontWeight: 600, border: 'none', py: 0.5 }}>{label}</TableCell>
                            <TableCell sx={{ border: 'none', py: 0.5 }}>{value}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, border: 'none', py: 0.5, color: '#81c784' }}>Total Payout</TableCell>
                          <TableCell sx={{ fontWeight: 700, border: 'none', py: 0.5, color: '#81c784' }}>{formatCurrency(payoutDetail.payoutRecord?.totalPayoutAmount)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              )}

              {detailTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Buyer</TableCell><TableCell>Workout</TableCell><TableCell>Gross</TableCell>
                        <TableCell>Net</TableCell><TableCell>Platform Fee</TableCell><TableCell>Athlete</TableCell>
                        <TableCell>Referral</TableCell><TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(payoutDetail.payments || []).map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{p.buyerId?.name || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{p.workoutId?.title || '-'}</TableCell>
                          <TableCell>{formatCurrency(p.amount)}</TableCell>
                          <TableCell>{formatCurrency(p.netAmount)}</TableCell>
                          <TableCell>{formatCurrency(p.platformFeeAmount)}</TableCell>
                          <TableCell>{formatCurrency(p.athleteAmount)}</TableCell>
                          <TableCell>{formatCurrency(p.referrerAmount)}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {detailTab === 2 && (
                payoutDetail.referralEarnings?.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Referred Trainer</TableCell><TableCell>Source Type</TableCell><TableCell>Gross</TableCell>
                          <TableCell>Athlete Net</TableCell><TableCell>Commission %</TableCell><TableCell>Commission Amount</TableCell><TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payoutDetail.referralEarnings.map((re, i) => (
                          <TableRow key={i}>
                            <TableCell>{re.referredTrainerId?.name || re.referredTrainerId || '-'}</TableCell>
                            <TableCell><Chip label={re.sourceType} size="small" sx={{ bgcolor: 'rgba(139,148,158,0.15)', color: '#8b949e', fontSize: '0.7rem' }} /></TableCell>
                            <TableCell>{formatCurrency(re.grossEarnings)}</TableCell>
                            <TableCell>{formatCurrency(re.athleteNetAmount)}</TableCell>
                            <TableCell>{re.commissionPercent}%</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(re.commissionAmount)}</TableCell>
                            <TableCell>{getStatusChip(re.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : <Typography sx={{ color: '#8b949e' }}>No referral earnings in this payout.</Typography>
              )}
            </Box>
          ) : <Typography sx={{ color: '#8b949e' }}>No data available.</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowPayoutDetailModal(false)} sx={{ color: '#8b949e' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payout Breakdown / Calculation Explanation Dialog */}
      <Dialog open={showBreakdownModal} onClose={handleCloseBreakdown} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon sx={{ color: '#2bb6a9' }} />
            <span>Payout Breakdown — {breakdownPayment?.sellerName}</span>
          </Box>
          <IconButton onClick={handleCloseBreakdown} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {breakdownPayment && (() => {
            const gross = Number(breakdownPayment.totalAmount || 0);
            const stripeFee = Number(breakdownPayment.totalStripeFee || 0);
            const net = Number(breakdownPayment.totalNetAmount || 0);
            const platformFee = Number(breakdownPayment.totalPlatformFee || 0);
            const athleteAmount = Number(breakdownPayment.totalAthleteAmount || 0);
            const referrerAmount = Number(breakdownPayment.totalReferrerAmount || 0);
            const agentAmount = Number(breakdownPayment.totalAgentAmount || 0);
            const referralCommEarned = Number(breakdownPayment.pendingReferralEarnings || 0);
            const finalPayout = athleteAmount + referralCommEarned;
            // Gross platform cut before referrer took their % of it
            const platformFeeGross = platformFee + referrerAmount;

            const incomePct = Number(breakdownPayment.sellerIncomePercentage || 80);
            const platformPct = 100 - incomePct;
            const referralPct = Number(breakdownPayment.sellerReferralCommissionPercent || 10);
            const agentPct = Number(breakdownPayment.sellerAgentCommissionPercent || 0);

            const stripeFeePctOfGross = gross > 0 ? (stripeFee / gross) * 100 : 0;
            const hasEstimated = (breakdownPayment.payments || []).some(p => p.stripeFeeSource === 'estimated');
            const hasActual = (breakdownPayment.payments || []).some(p => p.stripeFeeSource === 'actual');

            const rowSx = { border: 'none', py: 0.6 };
            const step = (n, title, body, color) => (
              <Paper key={n} sx={{ p: 2, mb: 1.5, border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                  <Chip size="small" label={`Step ${n}`} sx={{ bgcolor: 'rgba(43,182,169,0.12)', color: '#2bb6a9', fontWeight: 700, fontSize: '0.7rem' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: color || '#e4e6eb' }}>{title}</Typography>
                </Box>
                {body}
              </Paper>
            );
            const formulaRow = (label, formula, result, resultColor) => (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.4, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: '#e4e6eb' }}>{label}</Typography>
                  <Typography variant="caption" sx={{ color: '#8b949e', fontFamily: 'monospace' }}>{formula}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: resultColor || '#e4e6eb', whiteSpace: 'nowrap' }}>{result}</Typography>
              </Box>
            );

            return (
              <Box>
                <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(43,182,169,0.08)', color: '#e4e6eb', '& .MuiAlert-icon': { color: '#2bb6a9' } }}>
                  This shows exactly how <strong>${finalPayout.toFixed(2)}</strong> will be transferred to <strong>{breakdownPayment.sellerName}</strong> via Stripe when you click <em>Payout</em>. All numbers are snapshotted at payment time — later changes to income % or commissions don't rewrite history.
                </Alert>

                {/* Configuration snapshot */}
                <Paper sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Athlete Configuration</Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#8b949e', width: '40%' }}>Income share (of gross)</TableCell>
                        <TableCell sx={rowSx}><strong>{incomePct}%</strong> — platform keeps <strong>{platformPct}%</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#8b949e' }}>Referrer</TableCell>
                        <TableCell sx={rowSx}>{breakdownPayment.sellerHasReferrer ? <>Yes — referral commission <strong>{referralPct}%</strong> of Thravos's platform cut</> : <span style={{ color: '#8b949e' }}>None</span>}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#8b949e' }}>Agent</TableCell>
                        <TableCell sx={rowSx}>{breakdownPayment.sellerHasAgent ? <>{breakdownPayment.sellerAgentName || 'Yes'} — agent commission <strong>{agentPct}%</strong> of athlete income</> : <span style={{ color: '#8b949e' }}>None</span>}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>

                {/* Step 1 - Gross */}
                {step(1, 'Gross revenue (what the fan paid)', (
                  <>
                    <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.8 }}>
                      Sum of all pending Stripe payments received on behalf of this athlete.
                    </Typography>
                    {formulaRow('Total gross', 'Σ payment.amount', formatCurrency(gross), '#e4e6eb')}
                  </>
                ))}

                {/* Step 2 - Stripe fee */}
                {step(2, 'Stripe processing fee', (
                  <>
                    <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.8 }}>
                      Fetched from Stripe's <code>balance_transaction.fee</code> on each charge (the <em>actual</em> amount Stripe deducted — includes card-type surcharges like Amex/international/FX). Falls back to standard rates (e.g. USD 2.9% + $0.30) only if Stripe's fee isn't available yet.
                    </Typography>
                    {formulaRow('Total Stripe fee', 'Σ payment.stripeFeeAmount', `− ${formatCurrency(stripeFee)}`, '#e57373')}
                    <Typography variant="caption" sx={{ color: '#8b949e', display: 'block', mt: 0.5 }}>
                      Effective rate: <strong>{stripeFeePctOfGross.toFixed(2)}%</strong> of gross
                      {hasActual && <Chip size="small" label="actual" sx={{ ml: 1, height: 18, bgcolor: 'rgba(76,175,80,0.15)', color: '#81c784', fontSize: '0.65rem' }} />}
                      {hasEstimated && <Chip size="small" label="estimated" sx={{ ml: 1, height: 18, bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontSize: '0.65rem' }} />}
                    </Typography>
                  </>
                ))}

                {/* Step 3 - Net */}
                {step(3, 'Net amount (after Stripe)', (
                  <>
                    {formulaRow('Net available', 'gross − stripeFee', formatCurrency(net), '#e4e6eb')}
                  </>
                ))}

                {/* Step 4 - Platform fee */}
                {step(4, `Platform fee (${platformPct}% of gross)`, (
                  <>
                    <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.8 }}>
                      Thravos's cut before any referral commission. Calculated from <strong>gross</strong>, not net, using the athlete's snapshotted income % at the time of each sale.
                    </Typography>
                    {formulaRow('Platform fee (gross)', `gross × ${platformPct}% (= ${incomePct}% to athlete)`, `− ${formatCurrency(platformFeeGross)}`, '#2bb6a9')}
                  </>
                ))}

                {/* Step 5 - Athlete raw */}
                {step(5, 'Athlete income (after platform)', (
                  <>
                    {formulaRow("Athlete's starting share", 'net − platformFeeGross', formatCurrency(net - platformFeeGross), '#e4e6eb')}
                  </>
                ))}

                {/* Step 6 - Agent */}
                {breakdownPayment.sellerHasAgent && agentPct > 0 ? (
                  step(6, `Agent commission (${agentPct}% of athlete income)`, (
                    <>
                      <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.8 }}>
                        Paid separately to <strong>{breakdownPayment.sellerAgentName || 'the agent'}</strong>. Taken from athlete income only.
                      </Typography>
                      {formulaRow('Agent gets', `athleteIncome × ${agentPct}%`, `− ${formatCurrency(agentAmount)}`, '#ba68c8')}
                    </>
                  ))
                ) : (
                  step(6, 'Agent commission', (
                    <Typography variant="body2" sx={{ color: '#8b949e' }}>No agent configured — <strong>$0.00</strong>.</Typography>
                  ))
                )}

                {/* Step 7 - Referrer (from Thravos cut) */}
                {breakdownPayment.sellerHasReferrer && referrerAmount > 0 ? (
                  step(7, `Referrer commission (${referralPct}% of Thravos platform cut)`, (
                    <>
                      <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.8 }}>
                        Paid from <strong>Thravos's platform cut only</strong> — does not reduce athlete income. Only within the athlete's active referral window.
                      </Typography>
                      {formulaRow('Referrer gets', `platformFeeGross × ${referralPct}%`, `− ${formatCurrency(referrerAmount)}`, '#ffb74d')}
                      {formulaRow('Thravos keeps', 'platformFeeGross − referrer', formatCurrency(platformFee), '#2bb6a9')}
                    </>
                  ))
                ) : (
                  step(7, 'Referrer commission', (
                    <Typography variant="body2" sx={{ color: '#8b949e' }}>
                      {breakdownPayment.sellerHasReferrer ? 'Referral window inactive or referrer not payable — ' : 'No referrer — '}<strong>$0.00</strong>. Thravos keeps full platform cut: <strong>{formatCurrency(platformFee)}</strong>.
                    </Typography>
                  ))
                )}

                {/* Step 8 - Final athlete */}
                {step(8, "Athlete's final take-home", (
                  <>
                    {formulaRow('Athlete payout', 'athleteIncome − agent', formatCurrency(athleteAmount), '#81c784')}
                  </>
                ), '#81c784')}

                {/* Step 9 - Referral earnings this seller earned as a referrer */}
                {referralCommEarned > 0 && step(9, 'PLUS referral commissions earned as a referrer', (
                  <>
                    <Typography variant="body2" sx={{ color: '#8b949e', mb: 0.8 }}>
                      Amounts <strong>{breakdownPayment.sellerName}</strong> earned by referring other athletes. Bundled into the same Stripe transfer.
                    </Typography>
                    {formulaRow('Referral commissions earned', 'Σ pending referralEarnings.commissionAmount', `+ ${formatCurrency(referralCommEarned)}`, '#81c784')}
                  </>
                ))}

                {/* Final total */}
                <Paper sx={{ p: 2, mt: 1, border: '2px solid rgba(129,199,132,0.4)', bgcolor: 'rgba(129,199,132,0.06)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#81c784' }}>
                        Total Stripe transfer to {breakdownPayment.sellerName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8b949e', fontFamily: 'monospace' }}>
                        athletePayout + referralEarningsAsReferrer
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#81c784' }}>
                      {formatCurrency(finalPayout)}
                    </Typography>
                  </Box>
                </Paper>

                {/* Sanity check */}
                <Paper sx={{ p: 2, mt: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Sanity check — where every dollar of gross went</Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#e57373' }}>Stripe fee</TableCell>
                        <TableCell align="right" sx={rowSx}>{formatCurrency(stripeFee)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>{gross > 0 ? ((stripeFee / gross) * 100).toFixed(2) : '0.00'}%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#2bb6a9' }}>Platform</TableCell>
                        <TableCell align="right" sx={rowSx}>{formatCurrency(platformFee)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>{gross > 0 ? ((platformFee / gross) * 100).toFixed(2) : '0.00'}%</TableCell>
                      </TableRow>
                      {agentAmount > 0 && <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#ba68c8' }}>Agent</TableCell>
                        <TableCell align="right" sx={rowSx}>{formatCurrency(agentAmount)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>{gross > 0 ? ((agentAmount / gross) * 100).toFixed(2) : '0.00'}%</TableCell>
                      </TableRow>}
                      {referrerAmount > 0 && <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#ffb74d' }}>Referrer</TableCell>
                        <TableCell align="right" sx={rowSx}>{formatCurrency(referrerAmount)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>{gross > 0 ? ((referrerAmount / gross) * 100).toFixed(2) : '0.00'}%</TableCell>
                      </TableRow>}
                      <TableRow>
                        <TableCell sx={{ ...rowSx, color: '#81c784' }}>Athlete</TableCell>
                        <TableCell align="right" sx={rowSx}>{formatCurrency(athleteAmount)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>{gross > 0 ? ((athleteAmount / gross) * 100).toFixed(2) : '0.00'}%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, fontWeight: 700 }}>Sum</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, fontWeight: 700 }}>{formatCurrency(stripeFee + platformFee + agentAmount + referrerAmount + athleteAmount)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>100%</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ ...rowSx, fontWeight: 700, color: '#8b949e' }}>Expected (gross)</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, fontWeight: 700, color: '#8b949e' }}>{formatCurrency(gross)}</TableCell>
                        <TableCell align="right" sx={{ ...rowSx, color: '#8b949e' }}>—</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  {Math.abs((stripeFee + platformFee + agentAmount + referrerAmount + athleteAmount) - gross) > 0.05 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Sum doesn't match gross within $0.05 — likely rounding across many small payments. Investigate if the drift is larger.
                    </Alert>
                  )}
                </Paper>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseBreakdown} sx={{ color: '#8b949e' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingPayments;
